import React, { useState } from "react";
import { Heart, Trash2 } from "lucide-react";
import { api, storage, hasAdminToken } from "../lib/api";

export default function ReviewCard({ review, onDeleted }) {
  const [marked, setMarked] = useState(storage.getHelpfulMarks().has(review.id));
  const [count, setCount] = useState(review.helpful_count || 0);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = hasAdminToken();

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    // Optimistic
    const next = !marked;
    setMarked(next);
    setCount((c) => c + (next ? 1 : -1));
    storage.toggleHelpfulMark(review.id);
    try {
      const res = await api.markHelpful(review.id, storage.getDeviceId());
      setCount(res.helpful_count);
      setMarked(res.marked);
    } catch {
      // revert
      setMarked(!next);
      setCount((c) => c + (next ? -1 : 1));
      storage.toggleHelpfulMark(review.id);
    } finally {
      setBusy(false);
    }
  };

  const dateStr = (() => {
    try {
      return new Date(review.created_at).toLocaleDateString("es-CL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  })();

  return (
    <div
      className="bg-[#1A1A1A] border-2 border-[#333] p-4 space-y-3"
      data-testid={`review-item-${review.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display uppercase text-base">{review.user_handle || "anonimo"}</div>
          <div className="text-[#FEBA01] text-sm">
            {"★".repeat(review.rating)}
            <span className="text-[#333]">{"★".repeat(5 - review.rating)}</span>
          </div>
        </div>
        {dateStr && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#666] shrink-0">
            {dateStr}
          </div>
        )}
      </div>

      <p className="text-sm text-[#F5F5F5] whitespace-pre-line">{review.text}</p>

      {review.photo_base64 && (
        <img
          src={review.photo_base64}
          alt="Foto reseña"
          className="w-full max-h-72 object-cover border-2 border-[#333]"
          loading="lazy"
        />
      )}

      {review.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {review.tags.map((t) => (
            <span
              key={t}
              className="px-2 py-0.5 bg-[#0A0A0A] border border-[#333] text-[#FEBA01] font-mono text-[10px] uppercase tracking-wider"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-[#262626]">
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest transition ${
            marked ? "text-[#FEBA01]" : "text-[#A3A3A3] hover:text-[#FEBA01]"
          }`}
          data-testid={`review-helpful-${review.id}`}
        >
          <Heart size={14} className={marked ? "fill-[#FEBA01]" : ""} />
          Útil {count > 0 && <span className="ml-0.5">· {count}</span>}
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("¿Eliminar esta reseña definitivamente?")) return;
              setDeleting(true);
              try {
                await api.deleteReviewAdmin(review.id);
                onDeleted?.(review.id);
              } catch (ex) {
                alert(ex?.response?.data?.detail || "No pudimos eliminar.");
              } finally {
                setDeleting(false);
              }
            }}
            disabled={deleting}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white transition disabled:opacity-40"
            data-testid={`review-delete-${review.id}`}
            title="Eliminar (admin)"
          >
            <Trash2 size={11} /> {deleting ? "…" : "Eliminar"}
          </button>
        )}
      </div>
    </div>
  );
}
